export type TradeSide = 'buy' | 'sell'
export type TradeExitKind = 'exitPattern' | 'tpSl' | 'forceExit' | 'manual'

// The subset of a trade needed to classify how it was closed
export type TradeExitFields = {
  status?: string
  entryPrice?: number
  exitPrice?: number
  sl?: number
  tp?: number
  closeNotificationId?: string
  exitReason?: string
}

export const TRADE_EXIT_TOOLTIP: Record<TradeExitKind, string> = {
  tpSl: 'Closed by TP/SL',
  exitPattern: 'Closed by the exit pattern',
  forceExit: 'Closed by the time limit',
  manual: 'Closed manually',
}

// `sl` and `tp` are distances from entry, not absolute prices
export const getStopLossTakeProfitLevels = (trade?: TradeExitFields, side?: TradeSide) => {
  if (
    !trade ||
    !side ||
    trade.entryPrice === undefined ||
    trade.sl === undefined ||
    trade.tp === undefined
  ) {
    return { slPrice: undefined as number | undefined, tpPrice: undefined as number | undefined }
  }
  const slPrice = side === 'buy' ? trade.entryPrice - trade.sl : trade.entryPrice + trade.sl
  const tpPrice = side === 'buy' ? trade.entryPrice + trade.tp : trade.entryPrice - trade.tp
  return { slPrice, tpPrice }
}

// Treats near-misses as hits to absorb rounding, slippage and partial fills
export const didHitOrCrossLevel = (
  side: TradeSide,
  kind: 'sl' | 'tp',
  exitPrice?: number,
  levelPrice?: number
) => {
  if (exitPrice === undefined || levelPrice === undefined) return false
  const epsilon = Math.max(1e-4, Math.abs(levelPrice) * 1e-6)
  // BUY: SL below entry (hit when exit <= sl), TP above (hit when exit >= tp); SELL mirrored
  if (side === 'buy') {
    return kind === 'sl' ? exitPrice <= levelPrice + epsilon : exitPrice >= levelPrice - epsilon
  }
  return kind === 'sl' ? exitPrice >= levelPrice - epsilon : exitPrice <= levelPrice + epsilon
}

// How a CLOSED trade was closed; null while it is still open, rejected, or pending
//
// Recorded causes are read before prices are compared. Only TP/SL is inferred, and a close
// the engine ordered can land anywhere — including near a level by coincidence — so letting
// the price comparison run first would relabel it. `manual` is the residue: no recorded
// cause and no level reached.
export const getTradeExitKind = (
  trade: TradeExitFields | undefined,
  side: TradeSide
): TradeExitKind | null => {
  if (!trade || trade.status !== 'CLOSED' || trade.exitPrice === undefined) return null
  if (trade.closeNotificationId) return 'exitPattern'
  // Written by the close-position consumer when the exit engine's time-elapsed deadline fires
  if (trade.exitReason === 'force_exit') return 'forceExit'
  const { slPrice, tpPrice } = getStopLossTakeProfitLevels(trade, side)
  const slHit = didHitOrCrossLevel(side, 'sl', trade.exitPrice, slPrice)
  const tpHit = didHitOrCrossLevel(side, 'tp', trade.exitPrice, tpPrice)
  return slHit || tpHit ? 'tpSl' : 'manual'
}
